import { useDroppable } from '@dnd-kit/core';
import { format } from 'date-fns';
import { Typography } from '../../../../../core/components/molecules/Typography';
import { Button } from '../../../../../core/components/atoms/Button';
import DraggableDisciplineCard from '../DraggableDisciplineCard';
import { DisciplineMoveContentParams } from '../DisciplineActivityCard';
import {
  IScheduleArrays,
  ScheduleDayContent,
} from '../../../model/ScheduleHub';
import { useUserStore } from '../../../../Auth/stores/authStore';
import { formatTime } from '../../../utils/common';
import { add } from 'date-fns';

interface IScheduleDayCard {
  actualWeek?: IScheduleArrays;
  dayKey: string;
  day: string;
  date: Date;
  totalHours: number;
  activities: ScheduleDayContent[];
  onEditActivities: (params: {
    date: Date;
    dayKey: string;
    week: number;
  }) => void;
  onActivityCardClick?: (
    activity: ScheduleDayContent & { day: string; week: number }
  ) => void;
  onMoveContentSuccess?: () => void;
  onMoveContentConfirm?: (
    params: DisciplineMoveContentParams
  ) => Promise<void> | void;
}

const ScheduleDayCard = ({
  actualWeek,
  dayKey,
  day,
  date,
  totalHours,
  activities,
  onEditActivities,
  onActivityCardClick,
  onMoveContentSuccess,
  onMoveContentConfirm,
}: IScheduleDayCard) => {
  const { user } = useUserStore();

  // Trata a data como UTC para evitar problemas de timezone
  const dateObject = new Date(date);
  const utcDate = new Date(
    dateObject.getUTCFullYear(),
    dateObject.getUTCMonth(),
    dateObject.getUTCDate()
  );

  // Verifica se a data é hoje (normalizando para UTC para evitar problemas de timezone)
  const today = format(new Date(), 'yyyy-MM-dd');
  const cardDay = format(utcDate, 'yyyy-MM-dd');
  const isToday = today === cardDay;

  const { setNodeRef, isOver } = useDroppable({ id: dayKey });

  const formattedDate = format(utcDate, 'dd/MM');
  

  return (
    <div
      key={`${dayKey}-${actualWeek?.number}`}
      className={`
        flex h-[480px] min-w-[200px] max-w-[200px] flex-col gap-3 rounded-lg px-3 py-5 
        ${isToday ? 'bg-brand-color-primary-pure dark:bg-brand-color-primary-light' : 'bg-mono-color-lighter'}
      `}
    >
      <div>
        <div className='flex w-full flex-row items-center justify-between'>
          <Typography
            weight='bold'
            size='h6'
            type='title'
            className={`${isToday ? 'text-mono-color-white' : user?.isDarkMode ? 'text-mono-color-white' : 'text-mono-color-black'}`}
          >
            {day}
          </Typography>
          <Typography
            weight='bold'
            size='h6'
            type='title'
            className={`${isToday ? 'text-mono-color-white' : user?.isDarkMode ? 'text-mono-color-white' : 'text-mono-color-black'}`}
          >
            {formattedDate}
          </Typography>
        </div>
        <Typography
          size='small'
          className={`italic ${isToday ? 'text-mono-color-white' : user?.isDarkMode ? 'text-mono-color-white' : 'text-mono-color-black'}`}
        >
          Carga horária: {totalHours || 1}h
        </Typography>
      </div>
      <div
        ref={setNodeRef}
        className={`rounded flex max-h-full min-h-[80px] w-full flex-col gap-2 overflow-y-auto transition-colors ${isOver ? 'bg-brand-color-primary-pure/20' : ''}`}
      >
        {activities.map((activity, index) => {
          const clickPayload = {
            ...activity,
            day: dayKey,
            week: actualWeek?.number ?? 0,
          };
          const hasEssay = (activity?.essayNumber == 1);
          return (
            <DraggableDisciplineCard
              key={`${dayKey}-${activity.discipline}-${index}`}
              id={`${dayKey}-${index}`}
              dayKey={dayKey}
              activityIndex={index}
              activity={activity}
              clickPayload={clickPayload}
              onMoveContentSuccess={onMoveContentSuccess}
              onMoveContentConfirm={onMoveContentConfirm}
              onCardClick={onActivityCardClick}
              hasEssay={hasEssay}
            />
          );
        })}
      </div>
      <div className='my-1 w-full border-b-hairline border-b-mono-color-medium' />
      <Button
        data-tour="manage-subjects-button"
        variant='text'
        size='small'
        LeftIcon='edit_document'
        iconType='material'
        iconSize='small'
        full
        className='border-none bg-brand-color-secondary-pure text-[#fff] hover:bg-brand-color-secondary-pure/80'
        onClick={() =>
          onEditActivities({
            date,
            dayKey,
            week: actualWeek?.number ?? 0,
          })
        }
      >
        Gerenciar assuntos
      </Button>
    </div>
  );
};

export default ScheduleDayCard;
